import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import { TableSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const ENTRY_TYPE_LABELS = {
  general: "General Journal",
  contra: "Contra Entry",
  expense: "Expense Entry",
  income: "Income Entry",
  depreciation: "Depreciation",
  appreciation: "Appreciation",
  sales: "Sales Invoice",
  purchase: "Purchase Invoice",
  cash_receipt: "Cash Receipt",
  cash_disbursement: "Cash Disbursement",
  opening_balance: "Opening Balance",
  reversal: "Reversal",
};

const statusBadge = (status) => {
  const map = { draft: "warning", posted: "success", reversed: "secondary", void: "danger" };
  return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
};

const JournalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchEntry(); fetchFinancialYears(); }, [id]);

  const fetchEntry = async () => {
    setLoading(true);
    const res = await ajaxAccounting.getJournal({ id: parseInt(id) });
    if (res.status === "OK") setEntry(res.details);
    else toast.error(res.message || "Failed to load journal entry.");
    setLoading(false);
  };

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") setFinancialYears(res.details || []);
  };

  const postJournal = async () => {
    setPosting(true);
    const res = await ajaxAccounting.postJournal({ id: parseInt(id) });
    if (res.status === "OK") { toast.success("Journal posted!"); fetchEntry(); }
    else toast.error(res.message);
    setPosting(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this draft journal entry? This cannot be undone.")) return;
    setDeleting(true);
    const res = await ajaxAccounting.deleteJournal({ id: parseInt(id) });
    if (res.status === "OK") { toast.success("Journal entry deleted."); navigate("/accounting/journal"); }
    else { toast.error(res.message || "Failed to delete"); setDeleting(false); }
  };

  const entryYear = financialYears.find((y) => y.id === entry?.financial_year_id);
  const isYearOpen = entryYear?.status === "open";
  const canEdit = entry?.status === "draft" && financialYears.length > 0 && isYearOpen;

  const lines = entry?.lines || [];
  const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);

  const columns = [
    { key: "account_code", label: "Code", render: (v) => <span className="tx-12 tx-muted">{v}</span> },
    { key: "account_name", label: "Account", render: (v) => <strong>{v}</strong> },
    { key: "description", label: "Description", render: (v) => <span className="tx-12">{v || "-"}</span> },
    { key: "debit", label: "Debit (Dr)", render: (v) => v > 0 ? <span className="tx-success fw-bold">{functions.formatCurrency(v)}</span> : "-" },
    { key: "credit", label: "Credit (Cr)", render: (v) => v > 0 ? <span className="tx-danger fw-bold">{functions.formatCurrency(v)}</span> : "-" },
  ];

  return (
    <AppContainer
      title={entry ? `Journal Entry ${entry.reference || `#${entry.id}`}` : "Journal Entry"}
      previousPageTitle="Journal Entries"
      description="Full detail of this journal entry, including every debit and credit line posted to the ledger."
      actions={[
        { label: "Back to Journal", icon: "fa-solid fa-arrow-left", className: "btn btn-light", onClick: () => navigate("/accounting/journal") },
        ...(entry?.status === "draft" ? [{ label: posting ? "Posting..." : "Post to Ledger", icon: "fa-solid fa-check-double", className: "btn btn-primary", onClick: postJournal, disabled: posting }] : []),
        ...(entry?.status === "posted" ? [{ label: "Reverse", icon: "fa-solid fa-rotate-left", className: "btn btn-outline-warning", onClick: () => navigate(`/accounting/journal/${id}/reverse`) }] : []),
        ...(canEdit ? [
          { label: "Edit", icon: "fa-solid fa-pen", className: "btn btn-outline-primary", onClick: () => navigate(`/accounting/journal/new?type=${entry.type}&edit=${entry.id}`) },
          { label: deleting ? "Deleting..." : "Delete", icon: "fa-solid fa-trash", className: "btn btn-outline-danger", onClick: handleDelete, disabled: deleting },
        ] : []),
      ]}
    >
      <Toaster />

      {loading && <TableSkeleton rows={6} cols={5} />}

      {!loading && !entry && (
        <div className="card custom-card p-5 text-center tx-muted">Journal entry not found.</div>
      )}

      {!loading && entry && (
        <>
          <div className="row mb-3">
            <div className="col-lg-8">
              {/* h-100 on both cards in this row, so the two finish level. Only
                  the summary beside it had it, so this one stopped at its own
                  content and left the pair ending at different heights. */}
              <div className="card custom-card p-4 h-100">
                <div className="row g-3">
                  <div className="col-lg-4">
                    <div className="tx-11 tx-muted">Reference</div>
                    <div className="fw-semibold">{entry.reference || "-"}</div>
                  </div>
                  <div className="col-lg-4">
                    <div className="tx-11 tx-muted">Entry Date</div>
                    <div className="fw-semibold">{functions.formatDate(entry.entry_date)}</div>
                  </div>
                  <div className="col-lg-4">
                    <div className="tx-11 tx-muted">Type</div>
                    <div className="fw-semibold">{ENTRY_TYPE_LABELS[entry.type] || entry.type}</div>
                  </div>
                  <div className="col-lg-8">
                    <div className="tx-11 tx-muted">Description</div>
                    <div className="fw-semibold">{entry.description}</div>
                  </div>
                  <div className="col-lg-4">
                    <div className="tx-11 tx-muted">Status</div>
                    <div>{statusBadge(entry.status)}</div>
                  </div>
                  {entry.status === "draft" && entryYear && !isYearOpen && (
                    <div className="col-12">
                      <div className="alert alert-warning p-2 tx-12 mb-0">
                        <i className="fa-solid fa-lock me-1"></i>
                        This entry's financial year ({entryYear.name}) is {entryYear.status} — editing and deleting are disabled.
                      </div>
                    </div>
                  )}
                  {entry.narration && (
                    <div className="col-12">
                      <div className="tx-11 tx-muted">Narration</div>
                      <div className="tx-13">{entry.narration}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card custom-card p-3 h-100">
                <h6 className="fw-semibold mb-3">Summary</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span className="tx-13 tx-muted">Total Debit</span>
                  <span className="fw-bold tx-success">{functions.formatCurrency(totalDebit)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="tx-13 tx-muted">Total Credit</span>
                  <span className="fw-bold tx-danger">{functions.formatCurrency(totalCredit)}</span>
                </div>
                {entry.posted_at && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="tx-13 tx-muted">Posted At</span>
                    <span className="tx-13">{functions.formatDate(entry.posted_at)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card custom-card p-3">
            <h6 className="fw-semibold mb-3">Journal Lines</h6>
            <DataTable columns={columns} data={lines} loading={false} pageSize={25} emptyMessage="No lines on this entry." />
          </div>
        </>
      )}
    </AppContainer>
  );
};

export default JournalDetail;
